import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Grid
} from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';
import { makeStyles } from '@material-ui/styles';
import CloseIcon from '@material-ui/icons/Close';
import BuildIcon from '@material-ui/icons/Build';
import SettingsIcon from '@material-ui/icons/Settings';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import ExtensionIcon from '@material-ui/icons/Extension';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import TodayIcon from '@material-ui/icons/Today';
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet';
import BusinessIcon from '@material-ui/icons/Business';
import SupervisorAccountIcon from '@material-ui/icons/SupervisorAccount';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import DescriptionIcon from '@material-ui/icons/Description';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { getUser, moneyFormat } from 'src/utils/Common';
import moment from 'moment';

const useStyles = makeStyles(theme => ({
  paper: { borderRadius: 16, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0, 0, 0, .08)' },
  header: { padding: theme.spacing(3), backgroundColor: '#ffffff', borderBottom: '1px solid #f0f3f6' },
  headerContent: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  headerTitle: { display: 'flex', alignItems: 'center' },
  titleIconContainer: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 44, height: 44, marginRight: theme.spacing(2),
    color: '#3b82f6', borderRadius: 10, backgroundColor: '#eff6ff'
  },
  titleText: { color: '#0f172a', fontWeight: 800, fontSize: 22, fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' },
  subtitle: { display: 'block', marginTop: 4, color: '#2563eb', fontSize: 14, fontWeight: 700, fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' },
  dateBadge: {
    display: 'flex', alignItems: 'center', padding: '8px 14px',
    backgroundColor: '#eff6ff', border: '1px solid #dbeafe', borderRadius: 8,
    color: '#1e40af', fontSize: 13, fontWeight: 600, fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
  },
  dateIcon: { marginRight: 6, fontSize: 16, color: '#3b82f6' },
  content: { padding: theme.spacing(3), backgroundColor: '#f8fafc' },
  
  // Notice Banners
  infoBanner: {
    display: 'flex', alignItems: 'center', padding: '12px 18px',
    backgroundColor: '#eff6ff', border: '1px solid #dbeafe', borderRadius: 12,
    marginBottom: theme.spacing(3), color: '#1e40af', fontSize: 14, fontWeight: 500,
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
  },
  noteBanner: {
    display: 'flex', alignItems: 'center', padding: '12px 18px',
    backgroundColor: '#eff6ff', border: '1px solid #dbeafe', borderRadius: 12,
    marginTop: theme.spacing(3), color: '#1e40af', fontSize: 13, fontWeight: 500,
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
  },
  bannerIcon: { marginRight: 10, color: '#3b82f6', fontSize: 20 },

  // Table styling
  tableContainer: { border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', backgroundColor: '#ffffff', boxShadow: '0 4px 12px rgba(0, 0, 0, .02)' },
  table: { width: '100%' },
  tableHeaderCell: {
    backgroundColor: '#f1f5f9', color: '#334155', fontWeight: 700, fontSize: 13,
    padding: '16px 12px', borderBottom: '2px solid #e2e8f0', fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
  },
  tableBodyRow: { '&:hover': { backgroundColor: '#f8fafc' } },
  tableCell: { padding: '16px 12px', fontSize: 14, color: '#334155', borderBottom: '1px solid #f1f5f9', fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' },
  boldText: { fontWeight: 700, color: '#0f172a' },
  
  // Custom Badges for Material Types
  badgeLabour: { display: 'inline-flex', alignItems: 'center', padding: '4px 10px', borderRadius: 6, backgroundColor: '#faf5ff', color: '#7c3aed', fontWeight: 700, fontSize: 12, fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' },
  badgeSP: { display: 'inline-flex', alignItems: 'center', padding: '4px 10px', borderRadius: 6, backgroundColor: '#f0fdf4', color: '#16a34a', fontWeight: 700, fontSize: 12, fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' },
  badgeTransport: { display: 'inline-flex', alignItems: 'center', padding: '4px 10px', borderRadius: 6, backgroundColor: '#fff7ed', color: '#ea580c', fontWeight: 700, fontSize: 12, fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' },
  badgeExtra: { display: 'inline-flex', alignItems: 'center', padding: '4px 10px', borderRadius: 6, backgroundColor: '#f8fafc', color: '#475569', fontWeight: 700, fontSize: 12, fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' },
  badgeIcon: { marginRight: 6, fontSize: 14 },

  // Value formatting
  discountText: { color: '#ef4444', fontWeight: 700 },
  collectedText: { color: '#16a34a', fontWeight: 700 },

  // Totals Row
  totalsRow: {
    backgroundColor: '#f8fafc',
    '& td': {
      paddingTop: 20, paddingBottom: 20, color: '#0f172a', fontWeight: 800, fontSize: 14,
      borderTop: '2px solid #e2e8f0', borderBottom: 'none'
    }
  },

  // Summary Cards
  card: {
    padding: theme.spacing(2), border: '1px solid #e2e8f0', borderRadius: 16,
    backgroundColor: '#ffffff', display: 'flex', alignItems: 'center',
    boxShadow: '0 4px 12px rgba(0, 0, 0, .02)'
  },
  cardIconContainer: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 48, height: 48, borderRadius: 12, marginRight: theme.spacing(2)
  },
  iconCollected: { backgroundColor: '#dcfce7', color: '#16a34a' },
  iconDawlance: { backgroundColor: '#eff6ff', color: '#2563eb' },
  iconACS: { backgroundColor: '#ffedd5', color: '#ea580c' },
  iconDiscount: { backgroundColor: '#faf5ff', color: '#7c3aed' },
  cardContent: { display: 'flex', flexDirection: 'column' },
  cardLabel: { color: '#64748b', fontSize: 13, fontWeight: 600, fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' },
  cardValue: { color: '#0f172a', fontSize: 18, fontWeight: 800, margin: '2px 0', fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' },
  cardSubtext: { color: '#94a3b8', fontSize: 12, fontWeight: 500, fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' },

  actions: { padding: theme.spacing(2.5, 3), borderTop: '1px solid #f0f3f6', backgroundColor: '#ffffff' },
  closeBtn: { borderRadius: 8, padding: '8px 24px', textTransform: 'none', fontWeight: 700, fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' }
}));

function PaymentDetailsDialog({ open, onClose, workOrderId, data, openDate }) {
  const classes = useStyles();
  const { t } = useTranslation('work_order');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const sessionUser = getUser();
  const list = Array.isArray(data) ? data : [];

  const formatMoney = value => `${moneyFormat(value, sessionUser.TenantCode, sessionUser.CurrencyUnit)} ${sessionUser.CurrencyUnit}`;

  // Calculate totals
  const totalOriginal = list.reduce((sum, item) => sum + (Number(item.PriceVat) || 0), 0);
  const totalDiscount = list.reduce((sum, item) => sum + (Number(item.Discount) || 0), 0);
  const totalCollected = list.reduce((sum, item) => sum + (Number(item.TotalCollected) || 0), 0);
  const totalDawlanceShare = list.reduce((sum, item) => sum + (Number(item.DawlanceShare) || 0), 0);
  const totalACSShare = list.reduce((sum, item) => sum + (Number(item.ACSShare) || 0), 0);

  const renderBadge = (row) => {
    const materialTypeId = Number(row.MaterialTypeId);
    const typeLabel = t(row.MaterialType, row.MaterialType);

    if (materialTypeId === 1) {
      return (
        <span className={classes.badgeLabour}>
          <BuildIcon className={classes.badgeIcon} /> {typeLabel}
        </span>
      );
    } else if (materialTypeId === 2) {
      return (
        <span className={classes.badgeSP}>
          <SettingsIcon className={classes.badgeIcon} /> {typeLabel}
        </span>
      );
    } else if (materialTypeId === 3) {
      return (
        <span className={classes.badgeTransport}>
          <LocalShippingIcon className={classes.badgeIcon} /> {typeLabel}
        </span>
      );
    } else {
      return (
        <span className={classes.badgeExtra}>
          <ExtensionIcon className={classes.badgeIcon} /> {typeLabel}
        </span>
      );
    }
  };

  const formattedOpenDate = openDate ? moment(openDate).format('DD.MM.YYYY') : moment().format('DD.MM.YYYY');

  return (
    <Dialog
      open={open}
      fullWidth
      fullScreen={isMobile}
      maxWidth="lg"
      onClose={onClose}
      PaperProps={{ className: classes.paper }}
    >
      <DialogTitle className={classes.header}>
        <div className={classes.headerContent}>
          <div className={classes.headerTitle}>
            <span className={classes.titleIconContainer}><DescriptionIcon /></span>
            <div>
              <Typography className={classes.titleText}>{t('lblPaymentDetails', 'Payment Details')}</Typography>
              <Typography className={classes.subtitle}>
                {t('lblWorkOrderId', 'Work Order ID')}: <strong style={{ color: '#0f172a' }}>{workOrderId}</strong>
              </Typography>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span className={classes.dateBadge}>
              <TodayIcon className={classes.dateIcon} />
              {t('lblOpenDate', 'Open Date')}: {formattedOpenDate}
            </span>
            <IconButton size="small" onClick={onClose} style={{ marginLeft: 16 }}><CloseIcon /></IconButton>
          </div>
        </div>
      </DialogTitle>
      
      <DialogContent className={classes.content}>
        {/* Info Banner */}
        <Typography className={classes.infoBanner} variant="body2" component="div">
          <InfoOutlinedIcon className={classes.bannerIcon} />
          {t('lblPaymentDetailsBannerText', 'This section shows the price breakdown and payment shares for each item in the work order.')}
        </Typography>

        {/* Main Details Table */}
        <TableContainer className={classes.tableContainer}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeaderCell} align="center">#</TableCell>
                <TableCell className={classes.tableHeaderCell}>{t('lblMaterialType', 'Material Type')}</TableCell>
                <TableCell className={classes.tableHeaderCell}>{t('lblMaterial', 'Material')}</TableCell>
                <TableCell className={classes.tableHeaderCell} align="right">{t('lblPriceVat', 'Price (VAT Incl.)')}</TableCell>
                <TableCell className={classes.tableHeaderCell} align="right">{t('lblDiscount', 'Discount')}</TableCell>
                <TableCell className={classes.tableHeaderCell} align="right">{t('lblTotalCollected', 'Total Collected')}</TableCell>
                <TableCell className={classes.tableHeaderCell} align="right">{t('lblDawlanceShare', 'Dawlance Share')}</TableCell>
                <TableCell className={classes.tableHeaderCell} align="right">{t('lblASCShare', 'ASC Share')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" style={{ padding: '32px 0', color: '#64748b' }}>
                    <Typography variant="body2" style={{ color: '#64748b', fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' }}>
                      {t('lblNoPaymentDetailsFound', 'No payment details found for this work order.')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                list.map((row, index) => (
                  <TableRow className={classes.tableBodyRow} key={`${index}-${row.Material}`}>
                    <TableCell className={classes.tableCell} align="center" style={{ color: '#94a3b8', fontWeight: 600 }}>{index + 1}</TableCell>
                    <TableCell className={classes.tableCell}>{renderBadge(row)}</TableCell>
                    <TableCell className={`${classes.tableCell} ${classes.boldText}`}>{row.Material?.toUpperCase()}</TableCell>
                    <TableCell className={classes.tableCell} align="right">{formatMoney(row.PriceVat)}</TableCell>
                    <TableCell className={`${classes.tableCell} ${classes.discountText}`} align="right">{formatMoney(row.Discount)}</TableCell>
                    <TableCell className={`${classes.tableCell} ${classes.collectedText}`} align="right">{formatMoney(row.TotalCollected)}</TableCell>
                    <TableCell className={classes.tableCell} align="right">{formatMoney(row.DawlanceShare)}</TableCell>
                    <TableCell className={classes.tableCell} align="right">{formatMoney(row.ACSShare)}</TableCell>
                  </TableRow>
                ))
              )}
              {list.length > 0 && (
                <TableRow className={classes.totalsRow}>
                  <TableCell />
                  <TableCell colSpan={2} style={{ color: '#0f172a', fontWeight: 800 }}>{t('lblTotalUpper', 'TOTAL')}</TableCell>
                  <TableCell align="right" className={classes.boldText}>{formatMoney(totalOriginal)}</TableCell>
                  <TableCell align="right" className={classes.discountText}>{formatMoney(totalDiscount)}</TableCell>
                  <TableCell align="right" className={classes.collectedText}>{formatMoney(totalCollected)}</TableCell>
                  <TableCell align="right" className={classes.boldText}>{formatMoney(totalDawlanceShare)}</TableCell>
                  <TableCell align="right" className={classes.boldText}>{formatMoney(totalACSShare)}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Summary Metric Cards */}
        {list.length > 0 && (
          <Grid container spacing={3} style={{ marginTop: 24 }}>
            <Grid item xs={12} sm={6} md={3}>
              <div className={classes.card}>
                <div className={`${classes.cardIconContainer} ${classes.iconCollected}`}>
                  <AccountBalanceWalletIcon />
                </div>
                <div className={classes.cardContent}>
                  <Typography className={classes.cardLabel} variant="caption" component="span">{t('lblTotalCollected', 'Total Collected')}</Typography>
                  <Typography className={classes.cardValue} variant="h5" component="span">{formatMoney(totalCollected)}</Typography>
                  <Typography className={classes.cardSubtext} variant="caption" component="span">{t('lblFromCustomer', 'From Customer')}</Typography>
                </div>
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <div className={classes.card}>
                <div className={`${classes.cardIconContainer} ${classes.iconDawlance}`}>
                  <BusinessIcon />
                </div>
                <div className={classes.cardContent}>
                  <Typography className={classes.cardLabel} variant="caption" component="span">{t('lblDawlanceShare', 'Dawlance Share')}</Typography>
                  <Typography className={classes.cardValue} variant="h5" component="span">{formatMoney(totalDawlanceShare)}</Typography>
                  <Typography className={classes.cardSubtext} variant="caption" component="span">{t('lblPayableToDawlance', 'Payable to Dawlance')}</Typography>
                </div>
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <div className={classes.card}>
                <div className={`${classes.cardIconContainer} ${classes.iconACS}`}>
                  <SupervisorAccountIcon />
                </div>
                <div className={classes.cardContent}>
                  <Typography className={classes.cardLabel} variant="caption" component="span">{t('lblASCShare', 'ASC Share')}</Typography>
                  <Typography className={classes.cardValue} variant="h5" component="span">{formatMoney(totalACSShare)}</Typography>
                  <Typography className={classes.cardSubtext} variant="caption" component="span">{t('lblPayableToASC', 'Payable to ASC')}</Typography>
                </div>
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <div className={classes.card}>
                <div className={`${classes.cardIconContainer} ${classes.iconDiscount}`}>
                  <LocalOfferIcon />
                </div>
                <div className={classes.cardContent}>
                  <Typography className={classes.cardLabel} variant="caption" component="span">{t('lblTotalDiscount', 'Total Discount')}</Typography>
                  <Typography className={classes.cardValue} variant="h5" component="span" style={{ color: '#ef4444' }}>{formatMoney(totalDiscount)}</Typography>
                  <Typography className={classes.cardSubtext} variant="caption" component="span">{t('lblGivenDiscount', 'Given Discount')}</Typography>
                </div>
              </div>
            </Grid>
          </Grid>
        )}

        {/* Footer Note */}
        <Typography className={classes.noteBanner} variant="body2" component="div">
          <InfoOutlinedIcon className={classes.bannerIcon} />
          {t('lblPaymentDetailsFooterNote', 'Note: Dawlance Share is calculated based on material type rules and pricing effective on the work order open date.')}
        </Typography>
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button variant="outlined" onClick={onClose} className={classes.closeBtn}>{t('lblClose', 'CLOSE')}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default PaymentDetailsDialog;
